// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The evidence tier, client side. The tiers themselves are computed in
// pipeline/parse.mjs; this module is how they RENDER, and the rules are the
// same in all eight views.
//
// The governing idea: uncertainty never touches HUE and never touches
// LIGHTNESS. Fading an uncertain region toward white on a map reads as "low
// value" and is the commonest uncertainty lie in published data graphics. So
// uncertainty moves chroma toward the scale's neutral, and adds texture.

export type Tier = 1 | 2 | 3 | 4 | 5;

export const TIER_WORD: Record<Tier, string> = {
  1: 'firm',
  2: 'fair',
  3: 'weak',
  4: 'no basis',
  5: 'no estimate',
};

export const TIER_MEANING: Record<Tier, string> = {
  1: 'Firm — read the number.',
  2: 'Fair — read the number, allow a margin.',
  3: 'Weak — read the direction, not the number.',
  4: 'No basis — the survey cannot support a figure here.',
  5: 'No estimate — ABARES did not publish one.',
};

/** Tiers allowed into a ranking, a mean, a cumulative total or a superlative. */
export const RANKABLE_TIER = 2;

export const isRankable = (t: Tier | null | undefined): boolean => t != null && t <= RANKABLE_TIER;

/** Tier 3 readouts are tilde-prefixed: "~$41,200" rather than "$41,200". */
export function tierPrefix(t: Tier | null | undefined): string {
  return t === 3 ? '~' : '';
}

/**
 * Opacity for a mark of a given tier.
 *
 * Deliberately gentle at tier 2 — a fair estimate is still a usable estimate,
 * and washing it out would push readers toward the handful of firm cells and
 * make the site claim more certainty than it has, not less.
 */
export function tierOpacity(t: Tier | null | undefined): number {
  switch (t) {
    case 1: return 1;
    case 2: return 0.88;
    case 3: return 0.6;
    case 4: return 0.45;
    default: return 0.3;
  }
}

/** Tier 3 is outline-only: the mark shows position and sign, not magnitude. */
export const isOutlineOnly = (t: Tier | null | undefined): boolean => t === 3;

/** Tier 4 never encodes magnitude at all — it is drawn at a fixed token size. */
export const isTokenSized = (t: Tier | null | undefined): boolean => t === 4;

/** Tier 5 breaks a line or an area. Nothing is bridged or interpolated. */
export const isGap = (t: Tier | null | undefined): boolean => t === 5 || t == null;

/**
 * Apply the tier's chroma collapse to a hex colour.
 *
 * Mixes toward the scale's NEUTRAL (a warm paper grey), never toward white:
 * lightness is reserved for magnitude everywhere on this site, so borrowing it
 * for certainty would make an uncertain big number look like a certain small one.
 */
export function collapse(hex: string, t: Tier | null | undefined, neutral = '#8A8A82'): string {
  const k = t === 1 ? 0 : t === 2 ? 0.45 : t === 3 ? 0.8 : t === 4 ? 1 : 1;
  return mix(hex, neutral, k);
}

export function mix(a: string, b: string, k: number): string {
  const pa = hexToRgb(a); const pb = hexToRgb(b);
  if (!pa || !pb) return a;
  const c = (i: 0 | 1 | 2) => Math.round(pa[i] + (pb[i] - pa[i]) * Math.max(0, Math.min(1, k)));
  return `#${[c(0), c(1), c(2)].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

function hexToRgb(h: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(h.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * The three lines every tooltip on this site carries, in this order:
 * the value, then how many real farms are behind it, then the tier word.
 *
 * Frequency before percentage, always. "43 farms surveyed" is a fact a reader
 * can weigh; "RSE 22%" is not, for most of the audience.
 */
export function evidenceLines(sample: number | null | undefined, tier: Tier | null | undefined, rse?: number | null): string[] {
  const out: string[] = [];
  out.push(sample == null ? 'sample size not published' : `${sample} farm${sample === 1 ? '' : 's'} surveyed`);
  if (tier != null) out.push(TIER_MEANING[tier]);
  if (rse != null && Number.isFinite(rse)) out.push(`±${Math.round(Math.abs(rse))}% relative standard error`);
  return out;
}

/**
 * Is a difference between two survey estimates bigger than the noise on it?
 *
 * Mirrors `differenceIsMeaningful` in pipeline/parse.mjs. Where this is false
 * the comparison mark is drawn in neutral grey and its tooltip says so
 * verbatim, rather than in a colour that means "higher".
 *
 * This is a rough screen using standard errors added in quadrature. It is NOT a
 * hypothesis test and the site never calls the result a confidence interval —
 * an RSE is not a distribution, and treating it as one would be a normality
 * claim this data cannot support.
 */
export function differenceIsMeaningful(
  v1: number | null, rse1: number | null,
  v2: number | null, rse2: number | null,
): boolean {
  if (v1 == null || v2 == null || rse1 == null || rse2 == null) return false;
  const se1 = (Math.abs(rse1) / 100) * Math.abs(v1);
  const se2 = (Math.abs(rse2) / 100) * Math.abs(v2);
  return Math.abs(v1 - v2) >= Math.sqrt(se1 * se1 + se2 * se2);
}

export const NOT_MEANINGFUL_TIP = 'This difference is smaller than the survey error — do not act on it.';
